import { PropsWithChildren } from "react";

import { cn } from "@utils/ui";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

type ConfirmationModalProps = {
  type?: "info" | "warning";
  icon?: React.ReactNode;
  title: string;
  description: string;
  body?: React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmDisabled?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // A controlled caller with no trigger passes this to put focus back on
  // its own button when the modal closes.
  onCloseAutoFocus?: (event: Event) => void;
} & PropsWithChildren;
export const ConfirmationModal = ({
  type = "info",
  children,
  icon,
  title,
  description,
  body,
  confirmButtonText,
  cancelButtonText,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  isLoading = false,
  open,
  onOpenChange,
  onCloseAutoFocus,
}: ConfirmationModalProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* children is optional — a fully controlled caller (e.g. a trigger
          button that also needs to live inside its own Tooltip, which can't
          sit inside this asChild slot without breaking it) drives `open`
          itself and renders no trigger here. */}
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent
        className="rounded-2xl bg-background px-8 py-10"
        showCloseButton={false}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader className="flex flex-col items-center gap-8 text-center">
          {icon && (
            <figure
              className={cn(
                "grid size-24 items-center justify-center rounded-full",
                type === "warning" ? "bg-destructive/10" : "bg-primary-50"
              )}
            >
              {icon}
            </figure>
          )}

          <div>
            <DialogTitle className="text-center text-2xl font-semibold leading-relaxed [overflow-wrap:anywhere]">
              {title}
            </DialogTitle>
            <p className="text-center text-foreground-muted [overflow-wrap:anywhere]">
              {description}
            </p>
          </div>
        </DialogHeader>

        {body}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <DialogClose asChild>
            <Button
              variant="simple-outline"
              disabled={isLoading}
              className="whitespace-normal"
              onClick={onCancel}
            >
              {cancelButtonText || "Cancel"}
            </Button>
          </DialogClose>
          <Button
            variant={type === "warning" ? "destructive" : "default"}
            disabled={confirmDisabled}
            isLoading={isLoading}
            className="whitespace-normal"
            onClick={onConfirm}
          >
            {confirmButtonText || "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
